import type { GameState, ScoreEntry } from '../core/game';
import { unusedHintPoints } from '../core/game';
import { canonicalPath, lineCells } from '../core/board';
import { english, spanish } from '../core/language';
import type { WordPack } from '../content/packs';
import { recoverExposure, type ExposureHistory } from '../core/vocabulary';
import { recoverRewardViews, type RewardViewState } from '../content/reward-views';
import { challengeVersion, isChallenge, type Challenge } from '../core/challenge';
import { recoverThemes, type ThemeHistory } from '../content/theme-rotation';
import { addCompletion, emptyProgress, validProgress, validReceipt, type CompletionReceipt, type ProgressTotals } from '../core/progress';

export interface Preferences { locale: string; wordLocale: string; challenge: Challenge; askNextGame: boolean; sound: boolean; music: boolean; effectsVolume: number; musicVolume: number; reducedMotion: boolean; contrast: boolean; roomier: boolean; wake: boolean; tilt: boolean; pack: string; size: string; rows: number; columns: number }
export interface RecordEntry { boardId: string; group: string; score: number; when: number; targets?: string[]; wordCount?: number }
export interface Snapshot { version: 1; game: GameState | null; preferences: Preferences; records: RecordEntry[]; extraHints: number; grants: string[]; generatedPacks?: Array<WordPack & { title: string }>; exposure?: ExposureHistory; rewardViews?: RewardViewState; recentThemes?: ThemeHistory; progress?: ProgressTotals }
export const defaultPreferences: Preferences = { locale: 'en', wordLocale: 'en', challenge: 'balanced', askNextGame: false, sound: true, music: false, effectsVolume: 1, musicVolume: 1, reducedMotion: false, contrast: false, roomier: false, wake: false, tilt: true, pack: 'general', size: 'compact', rows: 9, columns: 11 };
export const defaultSnapshot = (): Snapshot => ({ version: 1, game: null, preferences: { ...defaultPreferences }, records: [], extraHints: 0, grants: [] });

function validEntry(entry: ScoreEntry, version: string): boolean {
  if (!entry || typeof entry !== 'object') return false;
  const count = entry.hintCount ?? 0, bonus = entry.unusedHints ?? 0, adCount = entry.adHintCount ?? 0;
  return denseArray(entry.words) && entry.words.length >= 1 && entry.words.length <= 2 && entry.words.every(word => text(word, 64))
    && ['base', 'target', 'special', 'completion', 'total'].every(key => Number.isSafeInteger(entry[key as keyof ScoreEntry]) && Number(entry[key as keyof ScoreEntry]) >= 0)
    && Number.isSafeInteger(adCount) && adCount >= 0 && (!adCount || version === 'classic-3')
    && Number.isSafeInteger(count) && count >= adCount && count - adCount <= 3 && Number.isSafeInteger(bonus) && bonus === count * unusedHintPoints
    && (!bonus || (version !== 'classic-1' && entry.completion > 0))
    && entry.total === entry.base + entry.target + entry.special + entry.completion + bonus;
}

export function validGame(value: unknown): value is GameState {
  if (!value || typeof value !== 'object') return false;
  const state = value as GameState;
  const board = state.board;
  if (board && (board.challenge !== undefined || board.challengeVersion !== undefined)
    && (!isChallenge(board.challenge) || !['challenge-1', challengeVersion].includes(board.challengeVersion ?? ''))) return false;
  if (state.schemaVersion !== 1 || !['classic-1', 'classic-2', 'classic-3'].includes(state.rulesVersion) || !board || !text(board.id, 128) || !text(board.seed, 128) || !['en', 'es'].includes(board.locale) || !text(board.packId, 128) || !text(board.packVersion, 64) || !text(board.dictionaryVersion, 128)) return false;
  if (!Number.isInteger(board.rows) || !Number.isInteger(board.columns) || board.rows < 3 || board.columns < 3 || board.rows > 32 || board.columns > 32 || board.rows * board.columns > 768) return false;
  const alphabet = board.locale === 'es' ? spanish.alphabet : english.alphabet;
  if (!denseArray(board.letters) || board.letters.length !== board.rows * board.columns || !board.letters.every(letter => alphabet.includes(letter))) return false;
  const validCells = (cells: number[]) => denseArray(cells) && cells.length >= 3 && cells.length <= 32 && cells.every(cell => Number.isInteger(cell) && cell >= 0 && cell < board.letters.length)
    && JSON.stringify(lineCells(board.rows, board.columns, cells[0]!, cells.at(-1)!)) === JSON.stringify(cells);
  if (!denseArray(board.targets) || board.targets.length === 0 || board.targets.length > 60 || !board.targets.every(target => target && text(target.word, 64) && validCells(target.cells) && target.cells.map(cell => board.letters[cell]).join('') === target.word) || new Set(board.targets.map(target => target.word)).size !== board.targets.length) return false;
  if (!denseArray(state.credited) || !denseArray(state.found) || !state.credited.every(word => text(word, 64)) || new Set(state.credited).size !== state.credited.length) return false;
  if (!denseArray(state.accepted) || !state.accepted.every(path => {
    if (!path || !validCells(path.cells) || path.key !== canonicalPath(path.cells) || !denseArray(path.words) || path.words.length < 1 || path.words.length > 2) return false;
    const forward = path.cells.map(cell => board.letters[cell]).join(''), reverse = [...path.cells].reverse().map(cell => board.letters[cell]).join('');
    return new Set(path.words).size === path.words.length && path.words.every(word => word === forward || word === reverse);
  }) || new Set(state.accepted.map(path => path.key)).size !== state.accepted.length) return false;
  if (JSON.stringify(state.accepted.flatMap(path => path.words)) !== JSON.stringify(state.credited)
    || JSON.stringify(state.credited.filter(word => board.targets.some(target => target.word === word))) !== JSON.stringify(state.found)) return false;
  if (!denseArray(state.ledger) || state.ledger.length !== state.accepted.length || !state.ledger.every((entry, index) => validEntry(entry, state.rulesVersion) && JSON.stringify(entry.words) === JSON.stringify(state.accepted[index]!.words))) return false;
  if (state.ledger.reduce((count, entry) => count + (entry.hintCount ?? 0) - (entry.adHintCount ?? 0), 0) > 3) return false;
  const completed = state.found.length === board.targets.length;
  if (state.ledger.some((entry, index) => entry.completion !== (completed && index === state.ledger.length - 1 ? 100 + 5 * board.targets.length : 0))) return false;
  return Number.isSafeInteger(state.score) && state.score === state.ledger.reduce((sum, entry) => sum + entry.total, 0)
    && Number.isInteger(state.hints) && state.hints >= 0 && state.hints <= 3 && (!completed || state.rulesVersion === 'classic-1' || state.hints === 0)
    && typeof state.assisted === 'boolean' && state.status === (completed ? 'completed' : 'playing');
}

function denseArray(value: unknown): value is unknown[] {
  if (!Array.isArray(value)) return false;
  const keys = Object.keys(value);
  return keys.length === value.length && keys.every((key, index) => key === String(index));
}
function object(value: unknown): Record<string, unknown> { return value && typeof value === 'object' && !Array.isArray(value) ? value as Record<string, unknown> : {}; }
function text(value: unknown, maximum: number): value is string { return typeof value === 'string' && value.length > 0 && value.length <= maximum; }
function validRecord(value: unknown): value is RecordEntry {
  const record = object(value);
  return text(record.boardId, 128) && text(record.group, 256) && Number.isSafeInteger(record.score) && Number(record.score) >= 0 && Number.isSafeInteger(record.when) && Number(record.when) >= 0;
}
function validInventory(value: unknown): value is number { return Number.isSafeInteger(value) && Number(value) >= 0 && Number.isSafeInteger(Number(value) * unusedHintPoints); }
function newerSave(value: unknown): boolean {
  const data = object(value), game = object(data.game);
  const generation = object(game.board).challengeVersion;
  const profile = typeof generation === 'string' ? /^challenge-(\d+)$/.exec(generation) : null;
  const rules = typeof game.rulesVersion === 'string' ? /^classic-(\d+)$/.exec(game.rulesVersion) : null;
  const progress = object(data.progress);
  return (typeof progress.version === 'number' && progress.version > 1) || (typeof data.version === 'number' && data.version > 1)
    || (typeof game.schemaVersion === 'number' && game.schemaVersion > 1) || !!(rules && Number(rules[1]) > 3) || !!(profile && Number(profile[1]) > 2);
}

export interface RecoveredSnapshot { snapshot: Snapshot; repaired: boolean }
export function recoverSnapshot(value: unknown, checkpoint?: unknown): RecoveredSnapshot {
  const current = object(value), previous = object(checkpoint);
  if (newerSave(current) || newerSave(previous)) throw new Error('Newer save format');
  const fallback = previous.version === 1 ? previous : {};
  const preferences = { ...defaultPreferences }, source = object(current.preferences), oldPreferences = object(fallback.preferences);
  for (const key of ['locale', 'wordLocale', 'challenge', 'askNextGame', 'size', 'pack', 'rows', 'columns', 'sound', 'music', 'effectsVolume', 'musicVolume', 'reducedMotion', 'contrast', 'roomier', 'wake', 'tilt'] as const) {
    const acceptable = (candidate: unknown): boolean => key === 'locale' || key === 'wordLocale' ? typeof candidate === 'string' && ['en', 'es'].includes(candidate)
      : key === 'challenge' ? isChallenge(candidate)
      : key === 'size' ? typeof candidate === 'string' && ['compact', 'roomy', 'expanded', 'custom'].includes(candidate)
      : key === 'pack' ? text(candidate, 128)
      : key === 'rows' || key === 'columns' ? Number.isInteger(candidate) && Number(candidate) >= 3 && Number(candidate) <= (key === 'rows' ? 32 : 24)
      : key === 'effectsVolume' || key === 'musicVolume' ? typeof candidate === 'number' && Number.isFinite(candidate) && candidate >= 0 && candidate <= 1
      : typeof candidate === 'boolean';
    const candidate = acceptable(source[key]) ? source[key] : acceptable(oldPreferences[key]) ? oldPreferences[key] : defaultPreferences[key];
    Object.assign(preferences, { [key]: candidate });
  }
  const game = current.game === null ? null : validGame(current.game) ? current.game : validGame(fallback.game) ? fallback.game : null;
  const gameSource = game === current.game ? current : fallback;
  const grants = [...new Set([...(Array.isArray(fallback.grants) ? fallback.grants : []), ...(Array.isArray(current.grants) ? current.grants : [])].filter(id => text(id, 128)))];
  const records = [...(Array.isArray(current.records) ? current.records : []), ...(Array.isArray(fallback.records) ? fallback.records : [])].filter(validRecord);
  const byBoard = new Map<string, RecordEntry>();
  for (const record of records) if (!byBoard.has(record.boardId)) byBoard.set(record.boardId, record);
  const generatedPacks = (Array.isArray(current.generatedPacks) ? current.generatedPacks : Array.isArray(fallback.generatedPacks) ? fallback.generatedPacks : []).filter(pack => {
    const item = object(pack), language = object(item.language);
    return text(item.id, 128) && text(item.version, 64) && text(item.title, 80) && ['en', 'es'].includes(String(language.locale))
      && Array.isArray(item.words) && item.words.length >= 5 && item.words.length <= 60 && item.words.every(word => text(word, 64));
  }).slice(-10) as Snapshot['generatedPacks'];
  const snapshot: Snapshot = { version: 1, game, preferences, records: [...byBoard.values()].sort((a, b) => a.when - b.when).slice(-100), grants,
    exposure: recoverExposure(fallback.exposure, current.exposure),
    rewardViews: recoverRewardViews(current.rewardViews ?? fallback.rewardViews),
    recentThemes: recoverThemes(fallback.recentThemes, current.recentThemes),
    extraHints: game?.status === 'playing' && validInventory(gameSource.extraHints) ? gameSource.extraHints : 0,
    ...(generatedPacks?.length ? { generatedPacks } : {}),
  };
  // Adding a missing preference uses its migration default without flagging corruption.
  const repaired = value !== undefined && (current.version !== 1 || (current.game !== null && game !== current.game)
    || Object.keys(preferences).some(key => key in source && source[key] !== preferences[key as keyof Preferences])
    || !current.preferences || typeof current.preferences !== 'object' || Array.isArray(current.preferences)
    || !Array.isArray(current.records) || !Array.isArray(current.grants) || !validInventory(current.extraHints)
    || (Array.isArray(current.grants) && current.grants.some(id => !text(id, 128)))
    || (Array.isArray(current.records) && current.records.some(record => !validRecord(record)))
    || (current.generatedPacks !== undefined && (!Array.isArray(current.generatedPacks) || current.generatedPacks.length !== generatedPacks?.length)));
  return { snapshot, repaired: repaired || (value === undefined && fallback.version === 1) };
}

export class Storage {
  progress: ProgressTotals = emptyProgress();
  private pendingCompletions: CompletionReceipt[] = [];
  private database?: Promise<IDBDatabase>;
  private writes: Promise<void> = Promise.resolve();
  private writeBlocked = false;
  private revision: unknown;
  private loaded?: { snapshot: unknown; checkpoint: unknown };
  notice: 'repaired' | 'newer' | 'unavailable' | 'content' | 'conflict' | null = null;
  private db(): Promise<IDBDatabase> {
    this.database ??= new Promise((resolve, reject) => {
      const request = indexedDB.open('wordhuli', 1);
      request.onupgradeneeded = () => request.result.createObjectStore('state');
      request.onsuccess = () => {
        request.result.onversionchange = () => { request.result.close(); this.database = undefined; };
        resolve(request.result);
      };
      request.onerror = () => { this.database = undefined; reject(request.error); };
    });
    return this.database;
  }
  async load(): Promise<Snapshot> {
    let values: { snapshot: unknown; checkpoint: unknown; progress?: unknown };
    try {
      const db = await this.db();
      values = await new Promise((resolve, reject) => {
        const transaction = db.transaction('state'), store = transaction.objectStore('state');
        const current = store.get('snapshot'), checkpoint = store.get('checkpoint'), revision = store.get('revision'), progress = store.get('lifetime-progress');
        let recoveredProgress = emptyProgress();
        progress.onsuccess = () => {
          if (validProgress(progress.result)) { recoveredProgress = { ...progress.result }; return; }
          recoveredProgress.limited = progress.result !== undefined;
          const cursor = store.openCursor(IDBKeyRange.bound('completion:', 'completion:\uffff'));
          cursor.onsuccess = () => {
            const row = cursor.result; if (!row) return;
            try {
              if (validReceipt(row.value)) recoveredProgress = addCompletion(recoveredProgress, row.value);
              else recoveredProgress.limited = true;
              row.continue();
            } catch { transaction.abort(); }
          };
        };
        transaction.oncomplete = () => { this.revision = revision.result; this.progress = recoveredProgress; resolve({ snapshot: current.result, checkpoint: checkpoint.result, progress: progress.result }); };
        transaction.onerror = transaction.onabort = () => reject(transaction.error);
      });
    } catch {
      this.writeBlocked = true; this.notice = 'unavailable'; return defaultSnapshot();
    }
    this.loaded = structuredClone(values);
    if ([values.snapshot, values.checkpoint, { progress: values.progress }].some(newerSave)) {
      this.writeBlocked = true; this.notice = 'newer'; return defaultSnapshot();
    }
    const recovered = recoverSnapshot(values.snapshot, values.checkpoint);
    this.pendingCompletions = recovered.snapshot.records.map(record => {
      const knownWords = Number.isSafeInteger(record.wordCount) && record.wordCount! >= 0;
      return { version: 1 as const, boardId: record.boardId, score: record.score, when: record.when,
        words: knownWords ? record.wordCount! : Array.isArray(record.targets) ? record.targets.slice(0, 60).filter(word => typeof word === 'string').length : 0,
        limited: !knownWords || recovered.snapshot.records.length >= 100 };
    }).filter(validReceipt);
    recovered.snapshot.progress = { ...this.progress };
    if (recovered.repaired) {
      this.notice = 'repaired';
      try { await this.preserveLoaded(); await this.save(recovered.snapshot); }
      catch { this.writeBlocked = true; this.notice = 'unavailable'; }
    }
    return recovered.snapshot;
  }

  async preserveLoaded(): Promise<void> {
    const data = structuredClone(this.loaded);
    this.writes = this.writes.catch(() => {}).then(async () => {
      const db = await this.db();
      await new Promise<void>((resolve, reject) => {
        const transaction = db.transaction('state', 'readwrite');
        transaction.objectStore('state').put({ ...data, when: Date.now() }, 'recovery-backup');
        transaction.oncomplete = () => resolve();
        transaction.onerror = transaction.onabort = () => reject(transaction.error);
      });
    });
    try { await this.writes; }
    catch (error) { this.writeBlocked = true; this.notice = 'unavailable'; throw error; }
  }

  async reset(): Promise<void> {
    // Called only after the player's existing reset confirmation.
    await this.write(defaultSnapshot(), true);
    this.writeBlocked = false; this.notice = null;
  }

  save(snapshot: Snapshot): Promise<void> {
    return this.write(snapshot);
  }
  async exportSnapshot(): Promise<Snapshot & { completionReceipts: CompletionReceipt[] }> {
    await this.writes;
    const db = await this.db();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction('state'), store = transaction.objectStore('state');
      const saved = store.get('snapshot'), totals = store.get('lifetime-progress');
      const receipts: CompletionReceipt[] = [];
      const cursor = store.openCursor(IDBKeyRange.bound('completion:', 'completion:\uffff'));
      cursor.onsuccess = () => { const row = cursor.result; if (!row) return; if (validReceipt(row.value)) receipts.push(row.value); row.continue(); };
      transaction.oncomplete = () => resolve({ ...saved.result, progress: totals.result, completionReceipts: receipts });
      transaction.onerror = transaction.onabort = () => reject(transaction.error);
    });
  }
  private write(snapshot: Snapshot, reset = false): Promise<void> {
    const data = structuredClone(snapshot);
    if (data.version !== 1 || (data.game !== null && !validGame(data.game))) return Promise.reject(new Error('Invalid saved game'));
    this.writes = this.writes.catch(() => {}).then(async () => {
      if (this.writeBlocked && !reset) throw new Error('Saved data is protected');
      const db = await this.db();
      await new Promise<void>((resolve, reject) => {
        const transaction = db.transaction('state', 'readwrite');
        let totals = reset ? emptyProgress() : { ...this.progress };
        const nextRevision = Array.from(crypto.getRandomValues(new Uint32Array(4)), value => value.toString(16)).join('-');
        transaction.oncomplete = () => {
          this.revision = nextRevision; this.progress = totals; snapshot.progress = { ...totals }; this.pendingCompletions = []; resolve();
        };
        transaction.onerror = () => reject(transaction.error);
        transaction.onabort = () => reject(transaction.error);
        try {
          const store = transaction.objectStore('state');
          const revision = store.get('revision');
          revision.onsuccess = () => {
            // IndexedDB serializes readwrite transactions across connections. Compare
            // and replace inside this same transaction so two tabs cannot both win.
            if (revision.result !== this.revision) {
              this.writeBlocked = true; this.notice = 'conflict';
              transaction.abort(); return;
            }
            try {
              if (reset) store.clear();
              const finish = () => {
                data.progress = { ...totals };
                store.put(totals, 'lifetime-progress'); store.put(data, 'snapshot');
                store.put(data, 'checkpoint'); store.put(nextRevision, 'revision');
              };
              const receipts = new Map((reset ? [] : this.pendingCompletions).map(receipt => [receipt.boardId, receipt]));
              if (data.game?.status === 'completed') {
                const boardId = data.game.board.id;
                receipts.set(boardId, { version: 1, boardId, score: data.game.score, words: data.game.credited.length,
                  when: data.records.find(record => record.boardId === boardId)?.when ?? Date.now(), limited: false });
              }
              let pending = receipts.size;
              if (!pending) finish();
              for (const receipt of receipts.values()) {
                const key = `completion:${receipt.boardId}`, existing = store.get(key);
                existing.onsuccess = () => {
                  try {
                    if (existing.result === undefined) { totals = addCompletion(totals, receipt); store.put(receipt, key); }
                    else if (!validReceipt(existing.result)) totals.limited = true;
                    if (--pending === 0) finish();
                  } catch (error) { transaction.abort(); reject(error); }
                };
              }
            } catch (error) { transaction.abort(); reject(error); }
          };
        } catch (error) { transaction.abort(); reject(error); }
      });
    });
    return this.writes;
  }
}
